import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { MetaDataAPI } from '../../api/client';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper to check if data is stale
const isDataStale = (lastFetched) => {
  if (!lastFetched) return true;
  return Date.now() - lastFetched > CACHE_DURATION;
};

// Async thunks with cache validation
export const fetchMetadataFields = createAsyncThunk(
  'metadata/fetchMetadataFields',
  async (params = {}, { rejectWithValue, getState }) => {
    const state = getState();
    const { data, lastFetched } = state.metadata.fields;

    // Return cached data if not stale
    if (data && !isDataStale(lastFetched)) {
      return data;
    }

    try {
      const result = await MetaDataAPI.fields(params);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch metadata fields');
    }
  }
);

export const fetchMetadataDistinct = createAsyncThunk(
  'metadata/fetchMetadataDistinct',
  async (params = {}, { rejectWithValue, getState }) => {
    const state = getState();
    const { data, lastFetched } = state.metadata.distinct;

    // Return cached data if not stale
    if (data && !isDataStale(lastFetched)) {
      return data;
    }

    try {
      const result = await MetaDataAPI.distinct(params);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch metadata distinct values');
    }
  }
);

// Force refresh thunk that bypasses cache
export const forceRefreshMetadata = createAsyncThunk(
  'metadata/forceRefreshMetadata',
  async (params = {}, { dispatch }) => {
    await Promise.all([
      dispatch(fetchMetadataFields(params)),
      dispatch(fetchMetadataDistinct(params))
    ]);
  }
);

const initialState = {
  fields: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
  },
  distinct: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
  },
};

const metadataSlice = createSlice({
  name: 'metadata',
  initialState,
  reducers: {
    clearMetadataErrors: (state) => {
      state.fields.error = null;
      state.distinct.error = null;
    },
    clearMetadataCache: (state) => {
      state.fields.data = null;
      state.fields.lastFetched = null;
      state.distinct.data = null;
      state.distinct.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Metadata Fields
      .addCase(fetchMetadataFields.pending, (state) => {
        // Only set loading if we don't have cached data
        if (!state.fields.data) {
          state.fields.loading = true;
        }
        state.fields.error = null;
      })
      .addCase(fetchMetadataFields.fulfilled, (state, action) => {
        state.fields.loading = false;
        state.fields.data = action.payload;
        state.fields.error = null;
        state.fields.lastFetched = Date.now();
      })
      .addCase(fetchMetadataFields.rejected, (state, action) => {
        state.fields.loading = false;
        state.fields.error = action.payload;
      })
      // Metadata Distinct
      .addCase(fetchMetadataDistinct.pending, (state) => {
        // Only set loading if we don't have cached data
        if (!state.distinct.data) {
          state.distinct.loading = true;
        }
        state.distinct.error = null;
      })
      .addCase(fetchMetadataDistinct.fulfilled, (state, action) => {
        state.distinct.loading = false;
        state.distinct.data = action.payload;
        state.distinct.error = null;
        state.distinct.lastFetched = Date.now();
      })
      .addCase(fetchMetadataDistinct.rejected, (state, action) => {
        state.distinct.loading = false;
        state.distinct.error = action.payload;
      })
      // Force refresh
      .addCase(forceRefreshMetadata.pending, (state) => {
        state.fields.loading = true;
        state.distinct.loading = true;
      })
      .addCase(forceRefreshMetadata.fulfilled, (state) => {
        state.fields.loading = false;
        state.distinct.loading = false;
      })
      .addCase(forceRefreshMetadata.rejected, (state) => {
        state.fields.loading = false;
        state.distinct.loading = false;
      });
  },
});

export const { clearMetadataErrors, clearMetadataCache } = metadataSlice.actions;
export default metadataSlice.reducer;
